// Cast/Crew refactor — actor management:
// This component was rewritten as part of moving cast/crew off free-text inputs and onto
// a structured `member_id` model backed by /admin/cast-crew-members.
// - Removed: 4 free-text fields (Director / Writer / Producer / Composer).
// - Added: Type (Cast | Crew), searchable Actor/Name dropdown (debounced, with inline "Add" → CastCrewMemberModal),
//          Role (auto-set "ACTOR" for Cast; multi-checkbox dropdown for Crew),
//          Character (Cast only).
// - Both dropdowns render via createPortal so they escape the parent accordion's `overflow: hidden`.
// - Selected entries render as Cast / Crew sections with avatar chips (image fallback to initials).
// - Parent state shape: { actorId, actorName, image_url, type, role, character, id }.
//   Crew `role` is a string array; Cast `role` is a single string.
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Accordion from "../../Accordian/Accordian";
import Advanced from "../mp4FileUpload/Advanced";
import style from "../Information/style.module.css";
import Button from "../button/Button";
import CastCrewMemberModal from "../CastCrewMemberModal/CastCrewMemberModal";
import { castCrewMemberList, getCastCrewRole } from "../../api/HttpApis";

const TYPE_OPTIONS = ["Cast", "Crew"];

const MemberAvatar = ({ src, name }) => {
  const [errored, setErrored] = useState(false);
  const initial = (name || "?").trim().charAt(0).toUpperCase() || "?";
  if (!src || errored) {
    return (
      <span className={style.memberChipAvatarFallback} aria-hidden="true">
        {initial}
      </span>
    );
  }
  return (
    <img
      src={src}
      alt=""
      className={style.memberChipAvatar}
      onError={() => setErrored(true)}
    />
  );
};
const IMDB_OPTIONS = Array.from({ length: 10 }, (_, i) => `${i + 1}.0`);
const RT_OPTIONS = Array.from({ length: 11 }, (_, i) => `${i * 10}%`);

const CastCrewMembers = ({
  movieData,
  handleCastCrewChange,
  selectedVideos,
  setSelectedVideos,
  setTrailer,
  setVideo,
  crewData,
  setCrewData,
  castMembers,
  setCastMembers,
  ratingsData,
  setRatingsData,
  soloProps,
}) => {
  const [castInput, setCastInput] = useState({
    actorName: "",
    actorId: null,
    character: "",
    role: "",
    type: "",
  });
  const [actorOptions, setActorOptions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalPrefillName, setModalPrefillName] = useState("");
  const [roleOptions, setRoleOptions] = useState([]);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [roleDropdownPos, setRoleDropdownPos] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const roleTriggerRef = useRef(null);
  const roleMenuRef = useRef(null);

  const updateDropdownPos = () => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    setDropdownPos({
      top: rect.bottom + window.scrollY + 4,
      left: rect.left + window.scrollX,
      width: rect.width,
    });
  };

  const updateRoleDropdownPos = () => {
    if (!roleTriggerRef.current) return;
    const rect = roleTriggerRef.current.getBoundingClientRect();
    setRoleDropdownPos({
      top: rect.bottom + window.scrollY + 4,
      left: rect.left + window.scrollX,
      width: rect.width,
    });
  };

  useLayoutEffect(() => {
    if (!showDropdown) return;
    updateDropdownPos();
    window.addEventListener("scroll", updateDropdownPos, true);
    window.addEventListener("resize", updateDropdownPos);
    return () => {
      window.removeEventListener("scroll", updateDropdownPos, true);
      window.removeEventListener("resize", updateDropdownPos);
    };
  }, [showDropdown]);

  useLayoutEffect(() => {
    if (!showRoleDropdown) return;
    updateRoleDropdownPos();
    window.addEventListener("scroll", updateRoleDropdownPos, true);
    window.addEventListener("resize", updateRoleDropdownPos);
    return () => {
      window.removeEventListener("scroll", updateRoleDropdownPos, true);
      window.removeEventListener("resize", updateRoleDropdownPos);
    };
  }, [showRoleDropdown]);

  useEffect(() => {
    getCastCrewRole()
      .then((res) => {
        const list =
          res?.data?.data?.map((item) => ({
            key: item.key,
            value: item.value,
          })) || [];
        setRoleOptions(list);
      })
      .catch(() => setRoleOptions([]));
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const inWrap = wrapRef.current && wrapRef.current.contains(e.target);
      const inDropdown =
        dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!inWrap && !inDropdown) {
        setShowDropdown(false);
      }

      const inRoleTrigger =
        roleTriggerRef.current && roleTriggerRef.current.contains(e.target);
      const inRoleMenu =
        roleMenuRef.current && roleMenuRef.current.contains(e.target);
      if (!inRoleTrigger && !inRoleMenu) {
        setShowRoleDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchActors = async (query) => {
    try {
      setSearching(true);
      const response = await castCrewMemberList(10, 1, query);
      const list =
        response?.data?.data?.items ||
        response?.data?.data?.data ||
        response?.data?.data ||
        [];
      setActorOptions(Array.isArray(list) ? list : []);
    } catch {
      setActorOptions([]);
    } finally {
      setSearching(false);
    }
  };

  const handleActorInputChange = (e) => {
    const value = e.target.value ?? "";
    setCastInput((prev) => ({ ...prev, actorName: value, actorId: null }));
    setShowDropdown(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchActors(value.trim());
    }, 300);
  };

  const handleActorFocus = () => {
    setShowDropdown(true);
    if (actorOptions.length === 0) {
      fetchActors((castInput.actorName || "").trim());
    }
  };

  const handleSelectActor = (actor) => {
    setCastInput((prev) => ({
      ...prev,
      actorName: actor.name,
      actorId: actor.id,
    }));
    setShowDropdown(false);
  };

  const openAddActorModal = () => {
    setModalPrefillName((castInput.actorName || "").trim());
    setModalOpen(true);
    setShowDropdown(false);
  };

  const handleActorCreated = (payload) => {
    const newActor =
      payload?.member ||
      payload?.cast_crew_member ||
      payload?.actor ||
      payload;
    if (newActor && newActor.name) {
      setCastInput((prev) => ({
        ...prev,
        actorName: newActor.name,
        actorId: newActor.id ?? null,
      }));
      setActorOptions((prev) => [newActor, ...prev]);
    }
    setModalOpen(false);
    setModalPrefillName("");
  };

  const toggleCrewRole = (roleKey) => {
    setCastInput((prev) => {
      const current = Array.isArray(prev.role) ? prev.role : [];
      const next = current.includes(roleKey)
        ? current.filter((r) => r !== roleKey)
        : [...current, roleKey];
      return { ...prev, role: next };
    });
  };

  const handleCastInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "type") {
      if (value === "Cast") {
        const actorRole =
          roleOptions.find((r) => r.value?.toLowerCase() === "actor") ||
          roleOptions.find((r) => r.key?.toUpperCase() === "ACTOR");
        setCastInput((prev) => ({
          ...prev,
          type: value,
          role: actorRole?.key || "ACTOR",
        }));
      } else if (value === "Crew") {
        setCastInput((prev) => ({
          ...prev,
          type: value,
          role: [],
          character: "",
        }));
      } else {
        setCastInput((prev) => ({ ...prev, type: value }));
      }
      return;
    }
    setCastInput((prev) => ({ ...prev, [name]: value }));
  };

  const addCastMember = () => {
    if (!(castInput.actorName || "").trim()) return;
    const newMember = { ...castInput, id: Date.now() };
    const updated = [...castMembers, newMember];
    setCastMembers(updated);
    setCastInput({
      actorName: "",
      actorId: null,
      character: "",
      role: "",
      type: "",
    });
    if (handleCastCrewChange) handleCastCrewChange(updated, []);
  };

  const removeCastMember = (id) => {
    const updated = castMembers.filter((c) => c.id !== id);
    setCastMembers(updated);
    if (handleCastCrewChange) handleCastCrewChange(updated, []);
  };

  const handleRatingsChange = (e) => {
    setRatingsData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const trimmedQuery = (castInput.actorName || "").trim();
  const crewRoleOptions = roleOptions.filter(
    (r) =>
      r.value?.toLowerCase() !== "actor" && r.key?.toUpperCase() !== "ACTOR"
  );
  const exactMatch = actorOptions.some(
    (a) => a.name?.toLowerCase() === trimmedQuery.toLowerCase()
  );

  return (
    <Accordion
      title="Cast & Crew"
      icon={<img src={`${process.env.PUBLIC_URL}/crew.png`} alt="" width={18} height={18} />}
      openId="crew"
      {...soloProps}
    >
      <div className={style.castCrewWrapper}>
        {/* ── Full Cast section label ── */}
        <p className={style.castSectionLabel}>Full Cast</p>

        {/* ── Cast input row: Type / Actor / Role / Character ── */}
        <div className={style.castInputRow}>
          <div className={style.castInputGroup}>
            <label className={style.castCrewLabel}>Type</label>
            <select
              name="type"
              className={style.castCrewSelect}
              value={castInput.type}
              onChange={handleCastInputChange}
            >
              <option value="">Select</option>
              {TYPE_OPTIONS?.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div className={style.castInputGroup}>
            <label className={style.castCrewLabel}>
              {castInput.type === "Crew" ? "Name" : "Actor Name"}
            </label>
            <div className={style.actorDropdownWrap} ref={wrapRef}>
              <input
                ref={inputRef}
                name="actorName"
                maxLength={80}
                className={style.castCrewInput}
                placeholder={
                  castInput.type === "Crew" ? "Name" : "Actor Name"
                }
                value={castInput.actorName}
                onChange={handleActorInputChange}
                onFocus={handleActorFocus}
                autoComplete="off"
              />
              {showDropdown &&
                createPortal(
                  <div
                    ref={dropdownRef}
                    className={style.actorDropdown}
                    style={{
                      position: "absolute",
                      top: dropdownPos.top,
                      left: dropdownPos.left,
                      width: dropdownPos.width,
                      zIndex: 10000,
                    }}
                  >
                    {actorOptions.map((actor) => (
                      <div
                        key={actor.id}
                        className={style.actorDropdownItem}
                        onClick={() => handleSelectActor(actor)}
                      >
                        {actor.image_url ? (
                          <img
                            src={actor.image_url}
                            alt={actor.name}
                            className={style.actorDropdownAvatar}
                          />
                        ) : (
                          <div className={style.actorDropdownAvatar} />
                        )}
                        <span>{actor.name}</span>
                      </div>
                    ))}
                    {searching && actorOptions.length === 0 && (
                      <div className={style.actorDropdownEmpty}>Searching…</div>
                    )}
                    {!searching &&
                      actorOptions.length === 0 &&
                      !trimmedQuery && (
                        <div className={style.actorDropdownEmpty}>
                          Start typing to search actors
                        </div>
                      )}
                    {trimmedQuery && !exactMatch && (
                      <div
                        className={`${style.actorDropdownItem} ${style.actorDropdownAdd}`}
                        onClick={openAddActorModal}
                      >
                        + Add "{trimmedQuery}"
                      </div>
                    )}
                  </div>,
                  document.body
                )}
            </div>
          </div>
          {castInput.type === "Crew" && (
            <div className={style.castInputGroup}>
              <label className={style.castCrewLabel}>Role</label>
              <div className={style.roleSelectWrap}>
                <button
                  type="button"
                  ref={roleTriggerRef}
                  className={style.roleSelectTrigger}
                  onClick={() => setShowRoleDropdown((v) => !v)}
                >
                  <span
                    className={`${style.roleSelectTriggerText} ${
                      !Array.isArray(castInput.role) ||
                      castInput.role.length === 0
                        ? style.roleSelectTriggerPlaceholder
                        : ""
                    }`}
                  >
                    {Array.isArray(castInput.role) &&
                    castInput.role.length > 0
                      ? castInput.role
                          .map(
                            (k) =>
                              roleOptions.find((r) => r.key === k)?.value || k
                          )
                          .join(", ")
                      : "Select roles"}
                  </span>
                  <span className={style.roleSelectCaret}>▾</span>
                </button>
                {showRoleDropdown &&
                  createPortal(
                    <div
                      ref={roleMenuRef}
                      className={style.roleSelectMenu}
                      style={{
                        position: "absolute",
                        top: roleDropdownPos.top,
                        left: roleDropdownPos.left,
                        width: roleDropdownPos.width,
                        zIndex: 10000,
                      }}
                    >
                      {crewRoleOptions?.map((r) => {
                        const selected =
                          Array.isArray(castInput.role) &&
                          castInput.role.includes(r.key);
                        return (
                          <div
                            key={r.key}
                            className={style.roleSelectOption}
                            onClick={() => toggleCrewRole(r.key)}
                          >
                            <span
                              className={`${style.roleSelectCheckbox} ${
                                selected
                                  ? style.roleSelectCheckboxChecked
                                  : ""
                              }`}
                            >
                              {selected ? "✓" : ""}
                            </span>
                            <span>{r.value}</span>
                          </div>
                        );
                      })}
                      {crewRoleOptions.length === 0 && (
                        <div className={style.actorDropdownEmpty}>
                          No roles available
                        </div>
                      )}
                    </div>,
                    document.body
                  )}
              </div>
            </div>
          )}
          {castInput.type !== "Crew" && (
            <div className={style.castInputGroup}>
              <label className={style.castCrewLabel}>Character</label>
              <input
                name="character"
                maxLength={80}
                className={style.castCrewInput}
                placeholder="Character Name"
                value={castInput.character}
                onChange={handleCastInputChange}
              />
            </div>
          )}
        </div>

        {/* ── Add Cast Member button ── */}
        <div className={style.castAddBtnRow}>
          <Button
            variant="dark"
            className={style.addTrailerBtn}
            onClick={addCastMember}
          >
            + Add Cast Member
          </Button>
        </div>

        {/* ── Cast / Crew sections ── */}
        {(() => {
          const castOnly = castMembers.filter((m) => m.type === "Cast");
          const crewOnly = castMembers.filter((m) => m.type === "Crew");

          const renderCastChip = (member) => (
            <span key={member.id} className={style.memberChip}>
              <MemberAvatar
                src={member.image_url}
                name={member.actorName}
              />
              <span className={style.memberChipName}>{member.actorName}</span>
              {member.character && (
                <span className={style.memberChipMeta}>
                  · {member.character}
                </span>
              )}
              <button
                type="button"
                className={style.memberChipRemove}
                onClick={() => removeCastMember(member.id)}
                aria-label="Remove"
              >
                ×
              </button>
            </span>
          );

          const renderCrewChip = (member) => {
            const roleKeys = Array.isArray(member.role)
              ? member.role
              : member.role
              ? [member.role]
              : [];
            const roleLabels = roleKeys.map(
              (k) => roleOptions.find((r) => r.key === k)?.value || k
            );
            const primary = roleLabels[0] || "";
            const extra = roleLabels.length - 1;
            const fullList = roleLabels.join(", ");
            return (
              <span
                key={member.id}
                className={style.memberChip}
                title={fullList}
              >
                <MemberAvatar
                  src={member.image_url}
                  name={member.actorName}
                />
                <span className={style.memberChipName}>{member.actorName}</span>
                {primary && (
                  <span className={style.memberChipMeta}>
                    · {primary}
                    {extra > 0 ? ` +${extra}` : ""}
                  </span>
                )}
                <button
                  type="button"
                  className={style.memberChipRemove}
                  onClick={() => removeCastMember(member.id)}
                  aria-label="Remove"
                >
                  ×
                </button>
              </span>
            );
          };

          return (
            <>
              {castOnly.length > 0 && (
                <div className={style.memberSection}>
                  <div className={style.memberSectionLabel}>Cast</div>
                  <div className={style.memberChipsRow}>
                    {castOnly.map(renderCastChip)}
                  </div>
                </div>
              )}
              {crewOnly.length > 0 && (
                <div className={style.memberSection}>
                  <div className={style.memberSectionLabel}>Crew</div>
                  <div className={style.memberChipsRow}>
                    {crewOnly.map(renderCrewChip)}
                  </div>
                </div>
              )}
            </>
          );
        })()}

        <CastCrewMemberModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onSuccess={handleActorCreated}
          editData={modalPrefillName ? { name: modalPrefillName } : null}
        />
      </div>
    </Accordion>
  );
};

export default CastCrewMembers;
